import IconRenderer from "@/components/feature/IconRenderer";
import { useTranslation } from "react-i18next";

export type BadgeItem = {
  label: string;
  icon?: string;
  color?: string;
};

type AdminListCardProps = {
  title: string;
  iconUrl?: string | null;
  iconFallback?: string;
  isActive?: boolean;
  onEdit?: () => void;
  onToggleStatus?: () => void;
  badges?: BadgeItem[];
  footerLeft?: React.ReactNode;
  customActions?: React.ReactNode;
};

export default function AdminListCard({
  title,
  iconUrl,
  iconFallback,
  isActive,
  onEdit,
  onToggleStatus,
  badges = [],
  footerLeft,
  customActions,
}: AdminListCardProps) {
  const { t } = useTranslation("common");

  return (
    <div
      onClick={onEdit}
      className={`bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800/80 rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-gray-200/80 dark:hover:border-zinc-700/80 transition-all relative flex flex-col gap-4 text-start ${
        onEdit ? "cursor-pointer active:scale-[0.985]" : ""
      }`}
    >
      {/* Top Header Row */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2.5 bg-gray-50 dark:bg-zinc-800/60 rounded-xl border border-gray-100 dark:border-zinc-700 shadow-sm shrink-0 flex items-center justify-center">
            {iconUrl ? (
              <IconRenderer iconUrl={iconUrl} size={20} />
            ) : (
              <i className={`${iconFallback || "ri-information-line"} text-xl text-teal-500`} />
            )}
          </div>
          <span className="font-bold text-gray-900 dark:text-zinc-100 text-base leading-snug truncate">{title}</span>
        </div>
        {isActive !== undefined && (
          <span
            className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold shrink-0 ${
              isActive
                ? "bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400"
                : "bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-400"
            }`}
          >
            {isActive ? t("adminActive") : t("adminInactive")}
          </span>
        )}
      </div>

      {/* Badges metadata list (Age Group, Category, etc.) */}
      {badges.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-t border-gray-50 dark:border-zinc-800/60 pt-4">
          {badges.map((badge, idx) => (
            <span
              key={idx}
              className="text-xs font-semibold text-gray-600 dark:text-zinc-300 bg-gray-50 dark:bg-zinc-800/80 border border-gray-100 dark:border-zinc-700 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5"
            >
              {badge.color && (
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0 border border-gray-200"
                  style={{ backgroundColor: badge.color }}
                />
              )}
              {badge.icon && <i className={`${badge.icon} text-sm text-gray-400 dark:text-zinc-500`} />}
              <span className="truncate">{badge.label}</span>
            </span>
          ))}
        </div>
      )}

      {/* Footer Actions & Info */}
      <div className="flex items-center justify-between border-t border-gray-50 dark:border-zinc-800/60 pt-3">
        <div className="flex-1 min-w-0">{footerLeft || <div />}</div>

        <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()} dir="ltr">
          {customActions ? (
            customActions
          ) : (
            <>
              {onEdit && (
                <button
                  onClick={onEdit}
                  className="p-2.5 text-gray-500 dark:text-zinc-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-zinc-800 rounded-xl border border-gray-100 dark:border-zinc-850 transition-colors"
                  title={t("adminEdit")}
                >
                  <i className="ri-edit-line text-lg" />
                </button>
              )}
              {onToggleStatus && (
                <button
                  onClick={onToggleStatus}
                  className={`p-2.5 rounded-xl border border-gray-100 dark:border-zinc-850 transition-colors ${
                    isActive
                      ? "text-amber-500 hover:bg-amber-50 dark:hover:bg-zinc-800"
                      : "text-emerald-500 hover:bg-emerald-50 dark:hover:bg-zinc-800"
                  }`}
                  title={isActive ? t("adminDisable") : t("adminEnable")}
                >
                  <i className={isActive ? "ri-close-circle-line text-lg" : "ri-checkbox-circle-line text-lg"} />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
